<template>
  <div class="direct-message-message-table">
    <div class="mb-x2" layout layout-align="space-between-start">
      <el-button type="default" @click="backToQueueList"
        ><i class="mdi mdi-keyboard-backspace"></i>一覧に戻る</el-button
      >
      <el-select v-model="status.value" placeholder="配信結果" @change="status.onChange">
        <el-option v-for="item in status.options" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <csv-download :disabled="!messages.length" :download="downloadCsv"></csv-download>
    </div>
    <el-table
      class="mb-x2"
      v-loading="loading"
      :data="messages"
      stripe
      @header-click="headerClick"
      empty-text="データがありません。"
      style="width: 100%"
    >
      <el-table-column label="配信開始日時" sort-by="-actionDatetime" :render-header="renderHeader" min-width="35%">
        <template slot-scope="scope">
          <div>{{ formatDatetime(scope.row.actionDatetime) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="ステータス" sort-by="-status" :render-header="renderHeader" min-width="25%">
        <template slot-scope="scope">
          <div class="text-success" v-if="scope.row.status === 'sent'">配信成功</div>
          <div v-if="scope.row.status === 'failed'">
            <div class="text-danger">配信失敗</div>
            <div class="error-type" v-if="scope.row.errorType">
              <span v-if="scope.row.errorType === 'unfollow'">(フォローされていない)</span
              ><span v-if="scope.row.errorType === 'user_not_exists'">(ユーザーが存在しない)</span
              ><span v-if="scope.row.errorType === 'not_found_serial_code'">(シリアルコード不足)</span>
            </div>
          </div>
          <div class="text-disabled" v-if="scope.row.status === 'unsent'">配信待ち</div>
        </template>
      </el-table-column>
      <el-table-column label="宛先アカウント" sort-by="-applicantName" :render-header="renderHeader" min-width="40%">
        <template slot-scope="scope">
          <applicant-name :applicant="scope.row.applicant"></applicant-name>
        </template>
      </el-table-column>
    </el-table>
    <pagination
      :total="total"
      :pagingNo="params.pagingNo"
      :limitCount="params.limitCount"
      :onChangePagingNo="onChangePagingNo"
      :onChangeLimitCount="onChangeLimitCount"
    ></pagination>
  </div>
</template>

<style lang="scss">
@use '@/client/assets/scss/variables.scss' as *;

.direct-message-message-table {
  .el-input__inner {
    height: $spacing-unit * 4;
  }

  .error-type {
    font-size: $font-small-size;
    color: $sub-text-color;
  }
}
</style>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment'
import { mapActions } from 'pinia'
import { defineComponent } from 'vue'

import ApplicantName from '@/client/components/_atoms/applicant_name'
import CsvDownload from '@/client/components/_atoms/csv_download'
import Pagination from '@/client/components/_atoms/pagination'
import api from '@/client/core/api'
import Util from '@/client/core/util'
import { formatDatetime } from '@/client/filters/filters'
import { useTwitterDirectMessageDialogStore } from '@/client/store/useTwitterDirectMessageDialogStore'
import { TGetMessageQueueMessages } from '@/typings/api/twitter_message_queues_message.type'

// パラメータ
type Params = {
  sortBy: string
  limitCount: number
  pagingNo: number
  status: string
}

export default defineComponent({
  props: {
    messageQueueId: Number
  },
  components: {
    ApplicantName,
    CsvDownload,
    Pagination
  },
  data() {
    return {
      messages: [],
      params: {
        status: '',
        sortBy: '-actionDatetime',
        pagingNo: 1,
        limitCount: 25
      },
      loading: false,
      total: 0,
      status: {
        value: '',
        options: [
          {
            label: '指定なし',
            value: 'none'
          },
          {
            label: '配信成功',
            value: 'sent'
          },
          {
            label: '配信失敗',
            value: 'failed'
          },
          {
            label: '配信待ち',
            value: 'unsent'
          }
        ],
        onChange: (value: string) => {
          this.status.value = value
          if (this.params.status === value) {
            return
          }
          if (value === 'none') {
            value = ''
          }
          this.changeParams({
            status: value
          })
        }
      }
    }
  },
  created() {
    // メッセージ一覧を取得
    this.getMessages()
  },
  watch: {
    // メッセージキューIDを変更する際、メッセージ一覧を取得
    messageQueueId() {
      this.getMessages()
    },
    // パラメータを変更する際、メッセージ一覧を取得
    params() {
      this.getMessages()
    }
  },
  methods: {
    formatDatetime,

    ...mapActions(useTwitterDirectMessageDialogStore, ['setQueueListIsShowDetail']),
    /**
     * メッセージ一覧を取得
     */
    async getMessages() {
      if (!this.messageQueueId) {
        return
      }
      this.loading = true
      const result: TGetMessageQueueMessages = await api.get(
        `/twitter_message_queues/${this.messageQueueId}/messages`,
        {
          params: this.params
        }
      )
      this.loading = false
      if (!result.data) {
        this.messages = []
        this.total = 0
        return
      }

      this.messages = result.data.messages
      this.total = result.data.dataCount
    },

    /**
     * メッセージキューに戻る
     */
    backToQueueList() {
      this.setQueueListIsShowDetail(false)
    },

    /**
     * パラメータを変更
     * @param {Params} params パラメータ
     */
    changeParams(params: Params) {
      if (!params.pagingNo) {
        params.pagingNo = 1
      }
      this.params = Object.assign({}, this.params, params)
    },

    /**
     * ソートを変更イベント
     * @param {string} sortBy ソート
     */
    onChangeSortBy(sortBy: string) {
      this.changeParams({
        sortBy
      })
    },

    /**
     * ページを変更イベント
     * @param {number} pagingNo ページング
     */
    onChangePagingNo(pagingNo: number) {
      this.changeParams({
        pagingNo
      })
    },

    /**
     * ページを変更イベント
     * @param {number} limitCount リミット数
     */
    onChangeLimitCount(limitCount: number) {
      this.changeParams({
        limitCount
      })
    },

    /**
     * CSVダウンロード
     */
    async downloadCsv() {
      const params = Object.assign({}, this.params, {
        limitCount: -1
      })
      const result: TGetMessageQueueMessages = await api.get(
        `/twitter_message_queues/${this.messageQueueId}/messages`,
        {
          params
        }
      )
      if (!result.data) {
        return {
          error: 'ERROR_LOADING'
        }
      }
      const messages = result.data.messages
      const filename = `campaign_messages_${moment().format('YYYY_MM_DD')}`
      const csvData = []
      csvData.push([
        '配信開始日時',
        '配信結果',
        'アカウント名',
        'アカウントスクリーンネーム',
        'アカウント画像',
        'フォロー中',
        '警告アカウント'
      ])

      // CSVデータを格納
      messages.forEach(message => {
        let status = ''
        switch (message.status) {
          case 'sent':
            status = '配信成功'
            break
          case 'failed':
            status = '配信失敗'
            break
          case 'unsent':
            status = '配信待ち'
            break
        }
        csvData.push([
          formatDatetime(message.actionDatetime),
          status,
          message.applicant.name,
          message.applicant.screenName,
          message.applicant.pictureUrl,
          message.applicant.isFollowing,
          message.applicant.isWarning
        ])
      })
      return {
        filename,
        data: csvData
      }
    },

    /**
     * ヘッダーをクリックするとソートする
     * @param {*} column
     */
    headerClick(column: any) {
      if (!column.sortBy) {
        return
      }
      if (column.sortBy.indexOf('-') === 0) {
        if (this.params.sortBy === column.sortBy) {
          this.onChangeSortBy(column.sortBy.substring(1))
        } else {
          this.onChangeSortBy(column.sortBy)
        }
      } else {
        if (this.params.sortBy === column.sortBy) {
          this.onChangeSortBy('-' + column.sortBy)
        } else {
          this.onChangeSortBy(column.sortBy)
        }
      }
    },

    /**
     * ヘッダー描画
     * @param {*} createElement
     * @param {*} { column }
     */
    renderHeader(createElement: any, { column }: any) {
      return Util.renderHeader(createElement, column.label, Util.getSortClass(this.params.sortBy, column.sortBy))
    }
  }
})
</script>
